import { Dispatch, SetStateAction, useRef, useState } from 'react';
import { CalendarCard } from './CalendarCard';
import { CallToBook } from './CallToBook';
import { TAvailItem } from '@/service/state/FullCalendar/domain';
import { TripTimesDialog } from './TripTimesDialog';
import useOnClickOutside from '@/service/hooks/useClickOutside';
import { TWeekDay } from '@/service/creators/createWeekDays';
import { ECalendarView } from '@/domain/Activities.domain';
import { TicketsContainer } from './TicketsContainer';
import { TicketsWidget } from '../../TicketsWidget/TicketsWidget';
import { useCalendarFullStore } from '@/service/state/FullCalendar/useFullCalendarStore';
import { useAvailabilityStore } from '@/service/state/Availability/useAvailabilityStore';
import { CalendarCardSkeleton } from './CalendarCardSkeleton';
import { ConfirmationPopup } from '@/ui/common/Popups/ConfirmationPopup';
import { scrollToId } from '@/service/utils/scrollToId';
import { ArrowWithTailIcon } from '@/ui/common/Icons/ArrowWithTailIcon';
import { usePartnerStore } from '@/service/state/Partner/usePartnerStore';

type TCalendarColumnProps = {
    list: TAvailItem[];
    selected: boolean;
    columnDate: TWeekDay;
    viewBy: ECalendarView;
    selectColumn: () => void;
    selectNextAvailDay: () => void;
    setHideButtons: Dispatch<SetStateAction<boolean>>;
};

export const CalendarColumn = ({
    list,
    selected,
    selectColumn,
    columnDate,
    viewBy,
    selectNextAvailDay,
    setHideButtons,
}: TCalendarColumnProps) => {
    const [selectedTrip, setSelectedTrip] = useState<TAvailItem>();
    const ref = useRef<HTMLDivElement>(null);
    const { showSchedule, showTickets } = useCalendarFullStore();
    const [showConfirmation, setShowConfirmation] = useState(false);

    const selectedSchedule = useAvailabilityStore((state) => state.selectedSchedule);
    const unavailableText = usePartnerStore((state) => state.texts?.['No activities on this day']);

    const noActivities = list?.length === 0;
    const isActivitiesView = viewBy === ECalendarView.ACTIVITIES;

    const handleClick = (trip: TAvailItem) => {
        scrollToId('calendar-viewport', 'start');

        if (isActivitiesView) {
            showSchedule(trip.id, columnDate.date, trip.need_ticket_names);
        } else {
            showTickets(trip.id, `${trip.schedule_id}`, columnDate.date);
        }

        setSelectedTrip(trip);
        setHideButtons(true);
        document.body.style.overflowY = 'hidden';
        selectColumn();
    };

    useOnClickOutside(ref, () => {
        setSelectedTrip(undefined);
        setHideButtons(false);
    });

    const isSchedulesView = isActivitiesView && !selectedSchedule && selectedSchedule !== 0;
    const closePopup = (skipConfirmation?: boolean) => {
        if (isSchedulesView || skipConfirmation) {
            setSelectedTrip(undefined);
            setHideButtons(false);
        } else {
            setShowConfirmation(true);
        }
        document.body.style.overflowY = 'auto';
    };

    return (
        <div
            className={`relative h-[500px] w-[354px] pb-14 pl-1 sm:mb-0 md:min-w-[188px] md:max-w-[196px] ${
                noActivities ? 'mb-20' : 'mb-4'
            }`}
            data-test-id="calendar-column"
        >
            {list?.[0]?.loading ? (
                <div className="mt-12 md:mt-12" data-test-id="calendar-skeleton">
                    <CalendarCardSkeleton />
                </div>
            ) : noActivities ? (
                <div
                    data-test-id="calendar-no-activities"
                    className="mt-11 h-full max-h-[612px] w-full max-w-full rounded-3xl bg-ash p-4 py-6 dark:bg-dark-3 md:max-w-[284px]"
                >
                    <h3 className="tx-heading-2 w-full text-center text-gray-1 md:tx-large-semibold dark:text-white">
                        {unavailableText || 'Online booking unavailable'}
                    </h3>
                    <p
                        className="tx-large-semibold relative mx-auto mt-2 w-fit min-w-[210px] cursor-pointer text-brand underline md:hidden"
                        onClick={selectNextAvailDay}
                    >
                        Go to next available date
                        <ArrowWithTailIcon className="absolute bottom-0 right-0 h-5 w-5 md:bottom-auto md:top-0" />
                    </p>
                    <p className="tx-large-medium mx-auto mt-2 w-fit text-gray-3 dark:text-dark-1 md:hidden">
                        Or
                    </p>
                    <CallToBook className="mx-auto mt-2 flex w-[210px] px-3 md:w-full" />
                    <div className="mx-auto mb-9 mt-12 flex h-[213px] w-full max-w-[224px] items-center justify-center bg-no-activities bg-contain bg-center bg-no-repeat dark:bg-no-activities-dark md:h-[130px]" />
                </div>
            ) : (
                <>
                    <div className="absolute left-1/2 top-6 z-10 h-[1px] w-[85%] -translate-x-1/2 bg-white shadow-[0px_2px_8px_8px] shadow-white dark:bg-blue-main dark:shadow-blue-main" />
                    <div
                        data-test-id="calendar-list"
                        className="custom-scroll relative mt-8 h-full w-full overflow-y-auto pl-[6px] pr-[3px]"
                    >
                        {list?.map((trip, index) => (
                            <CalendarCard
                                key={trip.slug + columnDate?.name}
                                schedule={trip}
                                isActive={
                                    selected &&
                                    trip.slug === selectedTrip?.slug &&
                                    trip.time === selectedTrip.time
                                }
                                selectColumn={() => handleClick(trip)}
                                index={index}
                            />
                        ))}
                    </div>
                    {selected && selectedTrip && (
                        <TicketsContainer onClose={closePopup} widthFull={!isSchedulesView}>
                            {isSchedulesView ? (
                                <TripTimesDialog trip={selectedTrip} />
                            ) : (
                                <>
                                    <TicketsWidget
                                        tripName={selectedTrip.name}
                                        agreement={selectedTrip.agreement}
                                        switchTime={isActivitiesView}
                                        hideSwitch={!isActivitiesView}
                                        calendarStatus={selectedTrip.calendarStatus}
                                        showCalendarStatuses
                                    />
                                    <ConfirmationPopup
                                        className="top-14 mt-0 md:top-1/2 md:mt-[-100px]"
                                        confirmText="Yes"
                                        cancelText="No"
                                        questionText="Are you sure you want to close?"
                                        infoText="You will lose your progress"
                                        isShown={showConfirmation}
                                        onHide={() => setShowConfirmation(false)}
                                        onConfirm={() => (
                                            setSelectedTrip(undefined),
                                            setShowConfirmation(false),
                                            setHideButtons(false)
                                        )}
                                        onCancel={() => setShowConfirmation(false)}
                                    />
                                </>
                            )}
                        </TicketsContainer>
                    )}
                    <div className="absolute bottom-6 left-1/2 z-10 h-[1px] w-[85%] -translate-x-1/2 bg-white shadow-[0px_-1px_8px_8px] shadow-white dark:bg-blue-main dark:shadow-blue-main" />
                </>
            )}
        </div>
    );
};
